// Scope types matching backend schema
export type ScopeType =
  | 'my_workstreams'
  | 'team'
  | 'company'
  | 'pillar'
  | 'person'
  | 'direct_reports'
  | 'custom';

export type GroupBy = 'pillar' | 'dri' | 'health' | 'role' | 'none' | null;

export type SortDirection = 'asc' | 'desc';

export type StatusValue =
  | 'on_track'
  | 'at_risk'
  | 'off_track'
  | 'complete'
  | 'cancelled'
  | 'missed'
  | 'archived'
  | 'no_status';

export const MISSING_INFORMATION_FILTER_OPTIONS = [
  'no_metrics',
  'no_milestones',
  'no_deliverables',
  'no_jira_initiatives',
  'no_workstream_objective',
] as const;

export type MissingInformationFilter = (typeof MISSING_INFORMATION_FILTER_OPTIONS)[number];

export const OUTCOMES_FILTER_OPTIONS = ['due_next_7_days', 'due_next_30_days', 'past_due'] as const;

export type OutcomesFilter = (typeof OUTCOMES_FILTER_OPTIONS)[number];

/**
 * Table timeframe buckets (OR semantics): one per timeframe filter key (see
 * `GET /api/v1/timeframes`), then the off-narrative buckets. URL `?timeframe=`
 * seeds a single timeframe's bucket; all loads use this array.
 */
export const TIMEFRAME_FILTER_OPTIONS = [
  'h1_fy26',
  'h2_fy26',
  'h1_fy27',
  'fy25_legacy',
  'enablement',
] as const;

export type TimeframeFilter = (typeof TIMEFRAME_FILTER_OPTIONS)[number];

export type AltitudeFilterValue = 'true' | 'false';

export const LAST_UPDATED_FILTER_OPTIONS = [
  'last_30_days',
  'last_90_days',
  'older_than_90_days',
  'show_all',
] as const;

export type LastUpdatedFilterValue = (typeof LAST_UPDATED_FILTER_OPTIONS)[number];

const MISSING_INFORMATION_FILTER_SET = new Set<string>(MISSING_INFORMATION_FILTER_OPTIONS);
const LAST_UPDATED_FILTER_SET = new Set<string>(LAST_UPDATED_FILTER_OPTIONS);
const TIMEFRAME_FILTER_SET = new Set<string>(TIMEFRAME_FILTER_OPTIONS);

export const sanitizeMissingInformationFilters = (
  values: unknown[] | undefined
): MissingInformationFilter[] | undefined => {
  if (!Array.isArray(values)) return undefined;
  const normalized = values.filter(
    (value): value is MissingInformationFilter =>
      typeof value === 'string' && MISSING_INFORMATION_FILTER_SET.has(value)
  );
  return normalized.length > 0 ? normalized : undefined;
};

export const sanitizeLastUpdatedFilters = (
  values: unknown[] | undefined
): LastUpdatedFilterValue[] | undefined => {
  if (!Array.isArray(values)) return undefined;
  const normalized = values.filter(
    (value): value is LastUpdatedFilterValue =>
      typeof value === 'string' && LAST_UPDATED_FILTER_SET.has(value)
  );
  return normalized.length > 0 ? normalized : undefined;
};

export const sanitizeTimeframeFilters = (
  values: unknown[] | undefined
): TimeframeFilter[] | undefined => {
  if (!Array.isArray(values)) return undefined;
  const normalized = values.filter(
    (value): value is TimeframeFilter =>
      typeof value === 'string' && TIMEFRAME_FILTER_SET.has(value)
  );
  return normalized.length > 0 ? normalized : undefined;
};

// Column configuration
export type ColumnId = 'workstream' | 'update' | 'metrics' | 'outcomes' | 'switchboard' | 'notes';

export interface ColumnConfig {
  id: ColumnId;
  label: string;
  hideable: boolean;
  defaultVisible: boolean;
}

export const COLUMN_CONFIGS: ColumnConfig[] = [
  {id: 'workstream', label: 'column_workstreams', hideable: false, defaultVisible: true},
  {id: 'metrics', label: 'column_metrics', hideable: true, defaultVisible: true},
  {id: 'update', label: 'column_update', hideable: true, defaultVisible: true},
  {id: 'switchboard', label: 'switchboard', hideable: true, defaultVisible: false},
  {id: 'outcomes', label: 'milestones', hideable: true, defaultVisible: true},
  {id: 'notes', label: 'column_notes', hideable: true, defaultVisible: false},
];

export interface SortOption {
  id: string;
  label: string;
}

export interface WorkstreamFilters {
  _version?: number;
  workstreams?: number[];
  health?: StatusValue[];
  pillar?: string[];
  altitude?: AltitudeFilterValue[];
  last_updated?: LastUpdatedFilterValue[];
  dri?: string[];
  exec_sponsor?: string[];
  team?: string[];
  role?: string[];
  missing_information?: MissingInformationFilter[];
  outcomes?: OutcomesFilter[];
  timeframes?: TimeframeFilter[];
}

// Group tab for horizontal grouping tabs
export interface GroupTab {
  id: string;
  label: string;
  count: number;
  disabled: boolean;
}

// Re-export rich types from auto-generated client
export type {
  RestrictedWorkstreamSummary,
  WorkstreamBasicWithSummary,
  WorkstreamsResponse,
  WorkstreamSwitchboardSummary,
} from 'client';

// Semantic status ordering to match legacy workstreams behavior.
export const STATUS_SEMANTIC_ORDER: readonly StatusValue[] = [
  'off_track',
  'at_risk',
  'no_status',
  'on_track',
  'missed',
  'cancelled',
  'complete',
  'archived',
];

// Status display configuration
export const STATUS_CONFIG: Record<StatusValue, {label: string; color: string}> = {
  on_track: {label: 'on_track', color: 'green'},
  at_risk: {label: 'at_risk', color: 'yellow'},
  off_track: {label: 'off_track', color: 'red'},
  complete: {label: 'complete', color: 'blue'},
  cancelled: {label: 'cancelled', color: 'gray'},
  missed: {label: 'missed', color: 'orange'},
  archived: {label: 'archived', color: 'gray'},
  no_status: {label: 'no_status', color: 'gray'},
};
